// src/components/audience/HourlyHeatmap.tsx
import React from 'react';
import { HourlyEngagement } from '../../types/audience';

interface HourlyHeatmapProps {
  data: HourlyEngagement[];
}

const HourlyHeatmap: React.FC<HourlyHeatmapProps> = ({ data }) => {
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({ length: 24 }, (_, i) => i);

  const getEngagementLevel = (day: number, hour: number): number => {
    const dataPoint = data.find(d => d.dayOfWeek === day && d.hour === hour);
    return dataPoint ? dataPoint.engagementLevel : 0;
  };

  const getHeatmapColor = (level: number): string => {
    // Convert level (0-1) to a red color intensity for heat representation
    const intensity = Math.round(level * 9); // 0-9 scale for Tailwind colors
    
    const colorMap = {
      0: 'bg-gray-100',
      1: 'bg-red-100',
      2: 'bg-red-200',
      3: 'bg-red-300',
      4: 'bg-red-400',
      5: 'bg-red-500',
      6: 'bg-red-600',
      7: 'bg-red-700',
      8: 'bg-red-800',
      9: 'bg-red-900'
    };
    
    return colorMap[intensity as keyof typeof colorMap] || 'bg-gray-100';
  };

  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>No heatmap data available</p>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto space-y-2">
      {/* Header row with day labels */}
      <div className="grid grid-cols-8 gap-1">
        <div className="text-xs text-gray-500 text-center font-medium">Hour</div>
        {days.map(day => (
          <div key={day} className="text-xs text-gray-500 text-center font-medium">
            {day}
          </div>
        ))}
      </div>

      {/* Heatmap rows - one row per hour */}
      {hours.map(hour => (
        <div key={hour} className="grid grid-cols-8 gap-1">
          <div className="text-xs text-gray-500 text-center py-1">
            {hour.toString().padStart(2, '0')}:00
          </div>
          {days.map((day, dayIndex) => {
            const level = getEngagementLevel(dayIndex, hour);
            return (
              <div
                key={`${dayIndex}-${hour}`}
                className={`h-6 rounded ${getHeatmapColor(level)} cursor-pointer transition-opacity hover:opacity-80`}
                title={`${day} ${hour}:00 - ${(level * 100).toFixed(0)}% engagement`}
              />
            );
          })}
        </div>
      ))}

      {/* Legend */}
      <div className="space-y-2 mt-4 pt-4 border-t">
        <div className="text-xs text-gray-600 font-medium text-center">
          Engagement Level (darker = more views)
        </div>
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Low</span>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-gray-100 rounded border border-gray-300"></div>
            <div className="w-3 h-3 bg-red-200 rounded"></div>
            <div className="w-3 h-3 bg-red-500 rounded"></div>
            <div className="w-3 h-3 bg-red-800 rounded"></div>
          </div>
          <span>High</span>
        </div>
      </div>
    </div>
  );
};

export default HourlyHeatmap;